import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { missions } from "../data/missions";

const SearchDialog = ({ open, onClose }) => {
    const { lang, t } = useLanguage();
    const [q, setQ] = useState("");
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 50);
            document.body.style.overflow = "hidden";
        } else {
            setQ("");
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape" && open) onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const results = useMemo(() => {
        const query = q.trim().toLowerCase();
        if (query.length < 2) return [];
        return missions.filter((m) => {
            return (
                String(m.year).includes(query) ||
                m.title_en.toLowerCase().includes(query) ||
                m.title_es.toLowerCase().includes(query) ||
                m.location_en.toLowerCase().includes(query) ||
                m.location_es.toLowerCase().includes(query) ||
                m.description_en.toLowerCase().includes(query) ||
                m.description_es.toLowerCase().includes(query)
            );
        });
    }, [q]);

    if (!open) return null;

    return (
        <div
            data-testid="search-dialog"
            className="fixed inset-0 z-50 flex items-start justify-center pt-24 px-4"
        >
            <div
                className="absolute inset-0 bg-white/90 backdrop-blur-md"
                onClick={onClose}
                aria-hidden
            />
            <div className="relative w-full max-w-2xl bg-white border border-[var(--sodeco-border)] rounded-md shadow-[0_30px_80px_-30px_rgba(0,0,0,0.2)] overflow-hidden">
                <div className="flex items-center gap-3 px-5 py-4 border-b border-[var(--sodeco-border)]">
                    <Search size={18} className="text-[var(--sodeco-muted)]" />
                    <input
                        ref={inputRef}
                        data-testid="search-input"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={t.actions.searchPlaceholder}
                        className="flex-1 bg-transparent text-lg font-serif placeholder:text-[var(--sodeco-muted)] focus:outline-none"
                    />
                    <button
                        data-testid="close-search-btn"
                        onClick={onClose}
                        aria-label={t.actions.close}
                        className="p-1.5 rounded-full hover:bg-[var(--sodeco-yellow-soft)]"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="max-h-[60vh] overflow-y-auto" data-testid="search-results">
                    {q.length >= 2 && results.length === 0 && (
                        <div className="px-5 py-8 text-sm text-[var(--sodeco-muted)]">
                            {t.actions.noResults}
                        </div>
                    )}
                    {results.length > 0 && (
                        <ul className="divide-y divide-[var(--sodeco-border)]">
                            <li className="px-5 py-2 text-[10px] uppercase tracking-[0.25em] text-[var(--sodeco-muted)]">
                                {results.length} {t.actions.results}
                            </li>
                            {results.map((r) => (
                                <li key={r.id}>
                                    <Link
                                        to="/missions"
                                        onClick={onClose}
                                        data-testid={`search-result-${r.id}`}
                                        className="flex items-center gap-4 px-5 py-4 hover:bg-[var(--sodeco-yellow-soft)] transition-colors"
                                    >
                                        <span
                                            className="font-serif text-2xl w-16"
                                            style={{ color: "var(--sodeco-yellow-deep)" }}
                                        >
                                            {r.year}
                                        </span>
                                        <div className="flex-1">
                                            <div className="font-serif text-lg">
                                                {lang === "es" ? r.title_es : r.title_en}
                                            </div>
                                            <div className="text-xs text-[var(--sodeco-muted)] uppercase tracking-wider">
                                                {lang === "es" ? r.location_es : r.location_en}
                                            </div>
                                        </div>
                                        <ArrowRight size={16} className="text-[var(--sodeco-muted)]" />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                    {q.length < 2 && (
                        <div className="px-5 py-8 text-sm text-[var(--sodeco-muted)]">
                            {t.actions.searchPlaceholder}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SearchDialog;
